import React from "react";

interface SlideContent {
  quote?: string;
  mainEmoji?: string;
  topEmoji?: string;
  bottomEmoji?: string;
  bgColor?: string;
  onEdit?: (content: SlideContent) => void;
}

const EditableDiv: React.FC<{
  text: string;
  onEdit: (value: string) => void;
  className?: string;
}> = ({ text, onEdit, className = "" }) => {
  return (
    <div
      contentEditable
      suppressContentEditableWarning
      onBlur={(e) => onEdit(e.currentTarget.textContent || "")}
      className={`outline-none hover:ring-2 hover:ring-purple-500/30 rounded px-2 transition-all ${className}`}
    >
      {text}
    </div>
  );
};

const QuoteSlide2 = {
  id: "quote-slide2",
  name: "Quote Slide 2",
  fields: [
    {
      id: "quote",
      label: "Quote Text",
      type: "text",
      defaultValue: "",
    },
    {
      id: "mainEmoji",
      label: "Main Emoji",
      type: "text",
      defaultValue: "🌠",
    },
    {
      id: "topEmoji",
      label: "Top Emoji",
      type: "text",
      defaultValue: "⭐",
    },
    {
      id: "bottomEmoji",
      label: "Bottom Emoji",
      type: "text",
      defaultValue: "✨",
    },
    {
      id: "bgColor",
      label: "Background Color",
      type: "text",
      defaultValue: "linear-gradient(135deg, #4c1d95 0%, #6d28d9 100%)",
    },
  ],
  render: (content: SlideContent = {}) => {
    const {
      quote = "",
      mainEmoji = "🌠",
      topEmoji = "⭐",
      bottomEmoji = "✨",
      bgColor = "linear-gradient(135deg, #4c1d95 0%, #6d28d9 100%)",
      onEdit = () => {},
    } = content;

    const handleEdit = (field: keyof SlideContent) => (value: string) => {
      const updatedContent = { ...content, [field]: value };
      onEdit(updatedContent);

      if (window.updateLiveConfig) {
        window.updateLiveConfig(updatedContent);
      }
    };

    return (
      <div
        className="w-full h-full relative overflow-hidden text-white"
        style={{ background: bgColor }}
      >
        {/* Background Pattern */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent" />
            {/* Star pattern */}
            {[...Array(20)].map((_, i) => (
              <div
                key={i}
                className="absolute w-1 h-1 bg-white rounded-full animate-twinkle"
                style={{
                  top: `${Math.random() * 100}%`,
                  left: `${Math.random() * 100}%`,
                  animationDelay: `${Math.random() * 2}s`,
                }}
              />
            ))}
          </div>
        </div>

        {/* Content Container */}
        <div className="relative z-10 flex flex-col items-center justify-center h-full p-12">
          {/* Top Emoji */}
          <EditableDiv
            text={topEmoji}
            onEdit={handleEdit("topEmoji")}
            className="text-5xl mb-8 transform hover:scale-110 transition-transform duration-300"
          />

          <div className="max-w-4xl w-full">
            {/* Main Quote Container */}
            <div
              className="relative bg-white/10 backdrop-blur-md rounded-3xl p-12 border border-white/20
                          shadow-[0_0_50px_rgba(139,92,246,0.3)] transform hover:scale-[1.01] transition-all duration-500"
            >
              {/* Main Emoji */}
              <div className="absolute -top-6 left-1/2 transform -translate-x-1/2">
                <EditableDiv
                  text={mainEmoji}
                  onEdit={handleEdit("mainEmoji")}
                  className="text-6xl transform hover:scale-110 transition-transform duration-300
                           hover:rotate-12"
                />
              </div>

              {/* Quote Text */}
              <div className="mt-6">
                <EditableDiv
                  text={quote}
                  onEdit={handleEdit("quote")}
                  className="text-4xl font-black leading-relaxed text-center text-purple-100
                           tracking-wide [text-wrap:balance] shadow-lg"
                />
              </div>

              {/* Decorative Lines */}
              <div className="absolute -left-4 top-1/2 w-8 h-px bg-gradient-to-r from-purple-500/50 to-transparent" />
              <div className="absolute -right-4 top-1/2 w-8 h-px bg-gradient-to-l from-purple-500/50 to-transparent" />
            </div>
          </div>

          {/* Bottom Emoji */}
          <EditableDiv
            text={bottomEmoji}
            onEdit={handleEdit("bottomEmoji")}
            className="text-5xl mt-8 transform hover:scale-110 transition-transform duration-300"
          />
        </div>

        {/* Corner Decorations */}
        <div className="absolute top-0 left-0 w-32 h-32 bg-gradient-to-br from-purple-500/20 to-transparent rounded-br-full" />
        <div className="absolute bottom-0 right-0 w-32 h-32 bg-gradient-to-tl from-purple-500/20 to-transparent rounded-tl-full" />
      </div>
    );
  },
};

export default QuoteSlide2;

// Add this to your global CSS or style block
const styles = `
@keyframes twinkle {
  0%, 100% { opacity: 0.2; }
  50% { opacity: 1; }
}

.animate-twinkle {
  animation: twinkle 3s infinite;
}
`;
